import { useState, useEffect, useRef } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
// import MDDropzone from "components/MDDropzone"; // Tidak digunakan lagi, menggunakan input file biasa

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

function DaftarDokumen() {
  const [dinasId, setDinasId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [bidangId, setBidangId] = useState(null);
  const [dokumenList, setDokumenList] = useState([]);
  const [filteredDokumenList, setFilteredDokumenList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKategori, setSelectedKategori] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limitPerPage, setLimitPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("terbaru"); // terbaru, terlama, nama
  const [viewMode, setViewMode] = useState("grid"); // grid, list
  const [kategoriStats, setKategoriStats] = useState({});

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  // const dropzoneRef = useRef(null); // Tidak digunakan lagi, menggunakan input file biasa
  const MAX_FILE_SIZE = 1.5 * 1024 * 1024; // 1.5 MB in bytes

  // Dialog PDF state (mengikuti pola dari daftar-penugasan)
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  // Form state
  const [form, setForm] = useState({
    nama: "",
    keterangan: "",
    kategori: "",
    scope: "bidang",
    bidang_id: null,
    dokumen: "", // untuk link eksternal
  });

  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Kategori options
  const kategoriOptions = [
    "perencanaan",
    "keuangan",
    "kepegawaian",
    "aset",
    "pengawasan",
    "teknis_bidang",
    "surat_keputusan",
    "regulasi",
    "personal",
    "lainnya",
  ];

  // Scope options
  const scopeOptions = ["publik", "pemda", "instansi", "bidang", "personal"];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      // Gunakan uid sebagai user_id, pastikan dalam format yang benar
      const userIdValue = user.uid
        ? String(user.uid)
        : user.user_id
        ? String(user.user_id)
        : null;
      setUserId(userIdValue);
      setBidangId(user.bidang_id);
      if (user.dinas_id && userIdValue) {
        fetchDaftarDokumen(user.dinas_id, userIdValue, 1);
      }
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (dinasId && userId) {
      setCurrentPage(1); // Reset ke halaman 1 saat filter berubah
      fetchDaftarDokumen(dinasId, userId, 1);
    }
  }, [
    selectedKategori,
    selectedScope,
    dinasId,
    userId,
    searchTerm,
    limitPerPage,
    sortBy,
  ]);

  // Tidak perlu useEffect untuk dropzone karena menggunakan input file biasa

  const fetchDaftarDokumen = async (idDinas, idUser, page = 1) => {
    setLoading(true);
    try {
      let url = `${API_ESA_OFFICE}dokumen?dinas_id=${idDinas}&user_id=${idUser}&page=${page}&limit=${limitPerPage}`;
      if (selectedKategori) {
        url += `&kategori=${selectedKategori}`;
      }
      if (selectedScope) {
        url += `&scope=${selectedScope}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await axios.get(url);
      let data = response.data.data || [];

      // Sorting berdasarkan sortBy
      if (sortBy === "terlama") {
        data = [...data].sort(
          (a, b) => new Date(a.tanggal_upload) - new Date(b.tanggal_upload)
        );
      } else if (sortBy === "nama") {
        data = [...data].sort((a, b) =>
          (a.nama || "").localeCompare(b.nama || "")
        );
      }
      // terbaru adalah default (sudah diurutkan dari backend)

      const totalData = response.data.total || 0;
      const totalPagesData = response.data.totalPages || 1;

      setDokumenList(data);
      setFilteredDokumenList(data);
      setTotal(totalData);
      setTotalPages(totalPagesData);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching daftar dokumen:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data dokumen",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler untuk perubahan halaman
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && dinasId && userId) {
      fetchDaftarDokumen(dinasId, userId, newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format waktu relatif (contoh: "3 bulan yang lalu")
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return "Baru saja";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} menit yang lalu`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} jam yang lalu`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `${diffInDays} hari yang lalu`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} bulan yang lalu`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} tahun yang lalu`;
  };

  const getKategoriColor = (kategori) => {
    const colors = {
      perencanaan: "info",
      keuangan: "success",
      kepegawaian: "warning",
      aset: "error",
      pengawasan: "secondary",
      teknis_bidang: "primary",
      surat_keputusan: "primary", // Changed from "dark" to "primary"
      regulasi: "info",
      personal: "default", // Changed from "light" to "default"
      lainnya: "default",
    };
    return colors[kategori] || "default";
  };

  const getScopeColor = (scope) => {
    const colors = {
      publik: "success",
      pemda: "info",
      instansi: "warning",
      bidang: "primary",
      personal: "secondary",
    };
    return colors[scope] || "default";
  };

  // useEffect untuk menghitung statistik per kategori
  useEffect(() => {
    if (dokumenList.length > 0) {
      const stats = {};
      kategoriOptions.forEach((kategori) => {
        stats[kategori] = 0;
      });

      // Hitung dari dokumenList yang ada
      dokumenList.forEach((dokumen) => {
        if (dokumen.kategori && stats.hasOwnProperty(dokumen.kategori)) {
          stats[dokumen.kategori] = (stats[dokumen.kategori] || 0) + 1;
        }
      });

      setKategoriStats(stats);
    } else {
      setKategoriStats({});
    }
  }, [dokumenList]);

  const handleOpenModal = (isEditMode = false, id = null) => {
    setIsEdit(isEditMode);
    setEditingId(id);
    if (isEditMode && id) {
      // Load data untuk edit
      const dokumen = dokumenList.find((d) => d.id_dokumen === id);
      if (dokumen) {
        const isExternalLink = dokumen.dokumen?.startsWith("http");
        setForm({
          nama: dokumen.nama || "",
          keterangan: dokumen.keterangan || "",
          kategori: dokumen.kategori || "",
          scope: dokumen.scope || "bidang",
          bidang_id: dokumen.bidang_id || null,
          dokumen: isExternalLink ? dokumen.dokumen : "",
        });
        // Jika bukan link eksternal, berarti file sudah ada di server (tidak perlu set selectedFile)
        setSelectedFile(null);
        setFileSizeError(false);
      }
    } else {
      // Reset form untuk tambah baru
      setForm({
        nama: "",
        keterangan: "",
        kategori: "",
        scope: "bidang",
        bidang_id: bidangId || null,
        dokumen: "",
      });
      setSelectedFile(null);
      setFileSizeError(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEdit(false);
    setEditingId(null);
    setForm({
      nama: "",
      keterangan: "",
      kategori: "",
      scope: "bidang",
      bidang_id: bidangId || null,
      dokumen: "",
    });
    setSelectedFile(null);
    setFileSizeError(false);
  };

  const handleEdit = (id) => {
    handleOpenModal(true, id);
  };

  // Mengikuti pola dari surat-masuk/index.js: menggunakan input file biasa
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setFileSizeError(false);
      return;
    }

    console.log("=== handleFileChange (input file) called ===");
    console.log("File:", file);
    console.log("File properties:", {
      name: file.name,
      size: file.size,
      type: file.type,
      isFile: file instanceof File,
    });

    // Validasi tipe file
    const allowedExtensions = [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".ppt",
      ".pptx",
    ];
    const fileExtension = "." + file.name.toLowerCase().split(".").pop();

    if (!allowedExtensions.includes(fileExtension)) {
      swal({
        title: "Format File Tidak Valid!",
        text: "Hanya file PDF, JPG, PNG, JPEG, Word, Excel, dan PowerPoint yang diizinkan.",
        icon: "error",
        button: "OK",
      });
      e.target.value = ""; // Reset input
      setSelectedFile(null);
      setFileSizeError(false);
      return;
    }

    // Validasi ukuran file (maksimal 1.5 MB)
    const maxSize = MAX_FILE_SIZE; // 1.5 MB
    if (file.size > maxSize) {
      swal({
        title: "Ukuran File Terlalu Besar!",
        text: `Ukuran file (${(file.size / 1024 / 1024).toFixed(
          2
        )} MB) melebihi batas maksimal 1.5 MB. Silakan gunakan link Google Drive untuk file yang lebih besar.`,
        icon: "error",
        button: "OK",
      });
      e.target.value = ""; // Reset input
      setSelectedFile(null);
      setFileSizeError(false);
      return;
    }

    // File valid, simpan ke state
    setFileSizeError(false);
    setSelectedFile(file);
    setForm((prev) => ({ ...prev, dokumen: "" }));
    console.log("✓ File berhasil disimpan ke state:", file.name);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileSizeError(false);
    // Reset input file
    const fileInput = document.getElementById("file-upload-dokumen");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleLinkChange = (e) => {
    const linkValue = e.target.value;
    setForm((prev) => ({ ...prev, dokumen: linkValue }));
    // Jika ada link, clear file dan error
    if (linkValue && linkValue.trim() !== "") {
      setSelectedFile(null);
      setFileSizeError(false);
    }
  };

  const handleSubmit = async () => {
    // Validasi field wajib
    if (!form.nama || !form.kategori || !form.scope) {
      swal({
        title: "Error",
        text: "Field nama, kategori, dan scope wajib diisi",
        icon: "error",
      });
      return;
    }

    // Mengikuti pola dari surat-masuk/index.js: langsung gunakan file dari state
    // File sudah divalidasi dan disimpan di handleFileChange
    const hasValidFile =
      selectedFile && selectedFile instanceof File && selectedFile.size > 0;
    const hasLink = form.dokumen && form.dokumen.trim() !== "";

    console.log("=== DEBUG: Validasi File ===");
    console.log("selectedFile:", selectedFile);
    console.log("selectedFile instanceof File:", selectedFile instanceof File);
    console.log("selectedFile size:", selectedFile?.size);
    console.log("hasValidFile:", hasValidFile);
    console.log("hasLink:", hasLink);
    console.log("dokumen (link):", form.dokumen);

    if (!hasValidFile && !hasLink) {
      swal({
        title: "Error",
        text: "Dokumen wajib diisi. Pilih salah satu: upload file (maks 1.5 MB) atau masukkan link eksternal (Google Drive, dll)",
        icon: "error",
      });
      return;
    }

    // Pastikan hanya salah satu yang aktif
    if (hasValidFile && hasLink) {
      // Jika keduanya ada, prioritas file (clear link)
      setForm((prev) => ({ ...prev, dokumen: "" }));
    }

    // Validasi ukuran file sebelum submit (jika ada file)
    // File sudah divalidasi di handleFileChange, tapi double check untuk memastikan
    if (hasValidFile && selectedFile) {
      const fileSize = selectedFile.size || 0;
      if (fileSize > MAX_FILE_SIZE) {
        swal({
          title: "File Terlalu Besar",
          text: `Ukuran file (${(fileSize / 1024 / 1024).toFixed(
            2
          )} MB) melebihi 1.5 MB. Silakan gunakan link Google Drive untuk file yang lebih besar.`,
          icon: "error",
        });
        setSubmitting(false);
        return;
      }
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("nama", form.nama);
      formData.append("keterangan", form.keterangan || "");
      formData.append("kategori", form.kategori);
      formData.append("scope", form.scope);
      formData.append("dinas_id", dinasId);
      // Pastikan user_id adalah integer yang valid
      const userIdInt = userId ? parseInt(userId) : null;
      if (!userIdInt || isNaN(userIdInt)) {
        swal({
          title: "Error",
          text: "User ID tidak valid. Silakan login kembali.",
          icon: "error",
        });
        setSubmitting(false);
        return;
      }
      formData.append("user_id", userIdInt);
      if (form.bidang_id) {
        formData.append("bidang_id", form.bidang_id);
      }

      // Pastikan hanya salah satu yang dikirim
      // Mengikuti pola dari surat-masuk/index.js
      if (hasValidFile && selectedFile) {
        // File sudah valid (sudah divalidasi di handleFileChange)
        formData.append("file", selectedFile);
        console.log("✓ File berhasil ditambahkan ke FormData:", {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type,
        });
        // Jangan kirim dokumen jika ada file
      } else if (hasLink && form.dokumen && form.dokumen.trim() !== "") {
        formData.append("dokumen", form.dokumen.trim());
        console.log("Mengirim link:", form.dokumen);
        // Jangan kirim file jika ada link
      } else {
        // Fallback: jika tidak ada file atau link, error (seharusnya tidak sampai sini karena sudah divalidasi di atas)
        console.error(
          "Tidak ada file atau link - ini seharusnya tidak terjadi!"
        );
        swal({
          title: "Error",
          text: "Dokumen wajib diisi. Pilih salah satu: upload file (maks 1.5 MB) atau masukkan link eksternal (Google Drive, dll)",
          icon: "error",
        });
        setSubmitting(false);
        return;
      }

      // Debug: log semua data yang akan dikirim
      console.log("FormData contents sebelum dikirim:");
      for (let pair of formData.entries()) {
        if (pair[1] instanceof File || pair[1] instanceof Blob) {
          console.log(pair[0] + ": [File]", {
            name: pair[1].name,
            size: pair[1].size,
            type: pair[1].type,
          });
        } else {
          console.log(pair[0] + ": ", pair[1]);
        }
      }

      let response;
      if (isEdit) {
        // Update
        console.log("Mengirim PUT request untuk update dokumen");
        response = await axios.put(
          `${API_ESA_OFFICE}dokumen/${editingId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // Create
        console.log("Mengirim POST request untuk create dokumen");
        response = await axios.post(`${API_ESA_OFFICE}dokumen`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }
      console.log("Response dari server:", response.data);

      if (response.data.success) {
        swal({
          title: "Berhasil",
          text: isEdit
            ? "Dokumen berhasil diupdate"
            : "Dokumen berhasil ditambahkan",
          icon: "success",
        });
        handleCloseModal();
        fetchDaftarDokumen(dinasId, userId, currentPage);
      } else {
        throw new Error(response.data.message || "Gagal menyimpan dokumen");
      }
    } catch (error) {
      console.error("Error submitting dokumen:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        request: error.request,
      });

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        (isEdit ? "Gagal mengupdate dokumen" : "Gagal menambahkan dokumen");

      swal({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, nama) => {
    const confirm = await swal({
      title: "Hapus Dokumen?",
      text: `Apakah Anda yakin ingin menghapus dokumen "${nama}"?`,
      icon: "warning",
      buttons: {
        cancel: "Batal",
        confirm: {
          text: "Hapus",
          value: true,
        },
      },
      dangerMode: true,
    });

    if (confirm) {
      try {
        await axios.delete(`${API_ESA_OFFICE}dokumen/${id}`);
        swal({
          title: "Berhasil",
          text: "Dokumen berhasil dihapus",
          icon: "success",
        });
        fetchDaftarDokumen(dinasId, userId, currentPage);
      } catch (error) {
        console.error("Error deleting dokumen:", error);
        swal({
          title: "Error",
          text: "Gagal menghapus dokumen",
          icon: "error",
        });
      }
    }
  };

  // Handler untuk membuka file (mengikuti pola dari daftar-penugasan)
  const handleFileClick = (dokumen) => {
    if (!dokumen.dokumen) return;

    let fileUrl = "";
    let fileName = dokumen.nama_file || "dokumen";

    if (dokumen.dokumen.startsWith("http")) {
      // Link eksternal - selalu buka di tab baru
      window.open(dokumen.dokumen, "_blank", "noopener,noreferrer");
      return;
    } else {
      // File internal
      fileUrl = `${API_ESA_OFFICE_UPLOAD}${dokumen.dokumen}`;
      const fileExtension =
        dokumen.dokumen.split(".").pop()?.toLowerCase() || "";
      const isPdf = fileExtension === "pdf";

      if (isPdf) {
        // PDF: buka di dialog (mengikuti pola dari daftar-penugasan)
        setSelectedFileUrl(fileUrl);
        setOpenFileDialog(true);
      } else {
        // Non-PDF: buka di tab baru
        window.open(fileUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  // Handler untuk menutup dialog PDF
  const handleCloseFileDialog = () => {
    setOpenFileDialog(false);
    setSelectedFileUrl(null);
  };

  const getFileIcon = (dokumen) => {
    if (!dokumen.dokumen)
      return <Icon fontSize="small">insert_drive_file</Icon>;

    if (dokumen.dokumen.startsWith("http")) {
      // Link eksternal
      return <Icon fontSize="small">link</Icon>;
    } else {
      // File internal
      const fileExtension =
        dokumen.dokumen.split(".").pop()?.toLowerCase() || "";
      if (fileExtension === "pdf") {
        return <Icon fontSize="small">picture_as_pdf</Icon>;
      }
      return <Icon fontSize="small">description</Icon>;
    }
  };

  // Fungsi untuk mendapatkan label tombol berdasarkan tipe file
  const getFileButtonLabel = (dokumen) => {
    if (!dokumen.dokumen) return "Tidak ada file";

    if (dokumen.dokumen.startsWith("http")) {
      return "Buka Link";
    } else {
      const fileExtension =
        dokumen.dokumen.split(".").pop()?.toLowerCase() || "";
      if (fileExtension === "pdf") {
        return "Buka PDF";
      }
      return "Download";
    }
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    { Header: "Nama Dokumen", accessor: "nama", width: "20%" },
    {
      Header: "Keterangan",
      accessor: "keterangan",
      width: "25%",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "300px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Kategori",
      accessor: "kategori",
      width: "12%",
      Cell: ({ value }) => {
        const chipColor = getKategoriColor(value) || "default";
        return (
          <Chip
            label={value || "-"}
            color={chipColor}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        );
      },
    },
    {
      Header: "Scope",
      accessor: "scope",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value || "-"}
          color={getScopeColor(value)}
          size="small"
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    { Header: "Bidang", accessor: "nama_bidang", width: "10%" },
    {
      Header: "Pengupload",
      accessor: "pengupload",
      width: "15%",
      Cell: ({ row }) => {
        const dokumen = row.original;
        const profileUrl = dokumen.uid_RSS
          ? `https://smartdh.rotendaokab.go.id/uploads/${dokumen.uid_RSS}.jpg`
          : null;
        return (
          <MDBox display="flex" alignItems="center" gap={1}>
            {profileUrl ? (
              <img
                src={profileUrl}
                alt={dokumen.nama_user || "User"}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #e0e0e0",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "grey.200",
                  color: "grey.500",
                }}
              >
                <Icon sx={{ fontSize: 20 }}>person</Icon>
              </MDBox>
            )}
            <MDTypography variant="caption" color="text">
              {dokumen.nama_user || "-"}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      Header: "File",
      accessor: "file",
      width: "8%",
      Cell: ({ row }) => (
        <IconButton
          size="small"
          color="info"
          onClick={() => handleFileClick(row.original)}
          title={
            row.original.dokumen?.startsWith("http")
              ? "Buka Link"
              : "Buka/Download File"
          }
        >
          {getFileIcon(row.original)}
        </IconButton>
      ),
    },
    { Header: "Tanggal Upload", accessor: "tanggal_upload", width: "10%" },
    {
      Header: "Aksi",
      accessor: "aksi",
      width: "10%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <IconButton
            size="small"
            color="warning"
            onClick={() => handleEdit(row.original.id_dokumen)}
          >
            <Icon fontSize="small">edit</Icon>
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() =>
              handleDelete(row.original.id_dokumen, row.original.nama)
            }
          >
            <Icon fontSize="small">delete</Icon>
          </IconButton>
        </MDBox>
      ),
    },
  ];

  const rows = filteredDokumenList.map((dokumen, index) => ({
    id_dokumen: dokumen.id_dokumen,
    no: index + 1,
    nama: dokumen.nama || "-",
    keterangan: dokumen.keterangan || "-",
    kategori: dokumen.kategori || "-",
    scope: dokumen.scope || "-",
    nama_bidang: dokumen.nama_bidang || "-",
    nama_user: dokumen.nama_user || "-",
    uid_RSS: dokumen.uid_RSS || null,
    dokumen: dokumen.dokumen || null,
    nama_file: dokumen.nama_file || null,
    tanggal_upload: formatDate(dokumen.tanggal_upload),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white" mb={0}>
                    Daftar Dokumen
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="white"
                    size="small"
                    onClick={() => handleOpenModal(false)}
                  >
                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                      add
                    </Icon>
                    Tambah Dokumen
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox pt={4} pb={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                >
                  <MDInput
                    label="Cari (Nama atau Keterangan)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      flexGrow: 1,
                      minWidth: 200,
                    }}
                  />
                  <Autocomplete
                    options={kategoriOptions}
                    value={selectedKategori}
                    onChange={(event, newValue) => {
                      setSelectedKategori(newValue);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Kategori"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 180,
                        }}
                      />
                    )}
                    getOptionLabel={(option) =>
                      option
                        .replace("_", " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())
                    }
                  />
                  <Autocomplete
                    options={scopeOptions}
                    value={selectedScope}
                    onChange={(event, newValue) => {
                      setSelectedScope(newValue);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Scope"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                    getOptionLabel={(option) =>
                      option.replace(/\b\w/g, (l) => l.toUpperCase())
                    }
                  />
                </MDBox>

                {/* Badge Informasi Total Dokumen dan Per Kategori */}
                {!loading && total > 0 && (
                  <MDBox
                    mx={2}
                    mb={2}
                    pt={2}
                    pb={2}
                    sx={{
                      borderTop: "1px solid #e0e0e0",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <MDBox mb={2}>
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color="text"
                        mb={1}
                      >
                        Informasi Dokumen
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexWrap="wrap"
                      gap={1.5}
                      alignItems="center"
                    >
                      {/* Badge Total Dokumen */}
                      <Chip
                        icon={<Icon sx={{ fontSize: 18 }}>description</Icon>}
                        label={`Total: ${total} Dokumen`}
                        color="info"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: "medium",
                          height: "32px",
                        }}
                      />
                      {/* Badge Per Kategori */}
                      {kategoriOptions.map((kategori) => {
                        const count = kategoriStats[kategori] || 0;
                        if (count === 0) return null;
                        const chipColor =
                          getKategoriColor(kategori) || "default";
                        return (
                          <Chip
                            key={kategori}
                            label={`${kategori
                              .replace("_", " ")
                              .replace(/\b\w/g, (l) =>
                                l.toUpperCase()
                              )}: ${count}`}
                            color={chipColor}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                        );
                      })}
                    </MDBox>
                  </MDBox>
                )}

                {/* Informasi dan Kontrol di Bagian Atas */}
                {!loading && (
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mx={2}
                    mb={2}
                    pt={2}
                  >
                    {/* Kiri: Dropdown Limit dan Sorting */}
                    <MDBox display="flex" gap={3} alignItems="flex-start">
                      <MDBox>
                        <MDBox mb={2} display="inline-block">
                          <MDTypography
                            component="label"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            textTransform="capitalize"
                          >
                            Per Halaman
                          </MDTypography>
                        </MDBox>
                        <Autocomplete
                          value={limitPerPage}
                          onChange={(event, newValue) => {
                            if (newValue !== null) {
                              setLimitPerPage(newValue);
                              setCurrentPage(1);
                            }
                          }}
                          options={[9, 10, 20, 50]}
                          renderInput={(params) => (
                            <MDInput {...params} variant="standard" />
                          )}
                          sx={{ minWidth: 160 }}
                        />
                      </MDBox>

                      <MDBox>
                        <MDBox mb={2} display="inline-block">
                          <MDTypography
                            component="label"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            textTransform="capitalize"
                          >
                            Urutkan
                          </MDTypography>
                        </MDBox>
                        <Autocomplete
                          value={sortBy}
                          onChange={(event, newValue) => {
                            if (newValue !== null) {
                              setSortBy(newValue);
                              setCurrentPage(1);
                            }
                          }}
                          options={["terbaru", "terlama", "nama"]}
                          getOptionLabel={(option) => {
                            const labels = {
                              terbaru: "Terbaru",
                              terlama: "Terlama",
                              nama: "Nama",
                            };
                            return labels[option] || option;
                          }}
                          renderInput={(params) => (
                            <MDInput {...params} variant="standard" />
                          )}
                          sx={{ minWidth: 160 }}
                        />
                      </MDBox>
                    </MDBox>

                    {/* Kanan: Toggle View */}
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(e, newView) => {
                        if (newView !== null) setViewMode(newView);
                      }}
                      size="large"
                    >
                      <ToggleButton value="grid" aria-label="grid view">
                        <Icon sx={{ fontSize: 28 }}>grid_view</Icon>
                      </ToggleButton>
                      <ToggleButton value="list" aria-label="list view">
                        <Icon sx={{ fontSize: 28 }}>view_list</Icon>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </MDBox>
                )}

                {/* Info Dataset dan Pagination */}
                {!loading && total > 0 && (
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                    mx={2}
                    mb={2}
                  >
                    <MDTypography variant="body2" color="text">
                      Menampilkan {(currentPage - 1) * limitPerPage + 1} sampai{" "}
                      {Math.min(currentPage * limitPerPage, total)} dari {total}{" "}
                      dokumen
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      Halaman {currentPage} dari {totalPages}
                    </MDTypography>
                  </MDBox>
                )}

                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox px={3}>
                    {filteredDokumenList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data dokumen
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          {searchTerm
                            ? "Tidak ada data yang sesuai dengan pencarian"
                            : "Belum ada dokumen yang terdaftar"}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                        {viewMode === "grid" ? (
                          // Tampilan Grid (kotak-kotak) - beberapa card dalam satu baris
                          <Grid container spacing={2}>
                            {filteredDokumenList.map((dokumen) => {
                              const profileUrl = dokumen.uid_RSS
                                ? `https://smartdh.rotendaokab.go.id/uploads/${dokumen.uid_RSS}.jpg`
                                : null;
                              // Cek apakah user yang login adalah pengupload dokumen
                              const isUploader =
                                userId &&
                                dokumen.user_id &&
                                String(userId) === String(dokumen.user_id);

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={dokumen.id_dokumen}
                                >
                                  <Card
                                    sx={{
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      borderRadius: 2,
                                      boxShadow: 2,
                                      backgroundColor: "#fff",
                                      transition: "box-shadow 0.3s",
                                      "&:hover": {
                                        boxShadow: 4,
                                      },
                                    }}
                                  >
                                    <MDBox
                                      p={2.5}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                      }}
                                    >
                                      {/* Tags di bagian atas */}
                                      <MDBox
                                        display="flex"
                                        gap={1}
                                        alignItems="center"
                                        mb={1.5}
                                        flexWrap="wrap"
                                      >
                                        <Chip
                                          label={
                                            dokumen.kategori
                                              ?.replace("_", " ")
                                              .replace(/\b\w/g, (l) =>
                                                l.toUpperCase()
                                              ) || "-"
                                          }
                                          size="small"
                                          sx={{
                                            backgroundColor: "#fff59d",
                                            color: "#000",
                                            fontWeight: "medium",
                                            fontSize: "0.75rem",
                                            borderRadius: "4px",
                                          }}
                                        />
                                        <Chip
                                          icon={
                                            <Icon sx={{ fontSize: 14 }}>
                                              description
                                            </Icon>
                                          }
                                          label={
                                            dokumen.scope?.replace(
                                              /\b\w/g,
                                              (l) => l.toUpperCase()
                                            ) || "-"
                                          }
                                          size="small"
                                          sx={{
                                            backgroundColor: "#fff",
                                            color: "#000",
                                            fontWeight: "medium",
                                            fontSize: "0.75rem",
                                            border: "1px solid #e0e0e0",
                                          }}
                                        />
                                      </MDBox>

                                      {/* Konten utama */}
                                      <MDBox
                                        flex={1}
                                        display="flex"
                                        flexDirection="column"
                                      >
                                        {/* Judul Dokumen */}
                                        <MDTypography
                                          variant="h6"
                                          fontWeight="bold"
                                          color="text"
                                          mb={1}
                                          sx={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            minHeight: "3rem",
                                          }}
                                        >
                                          {dokumen.nama || "-"}
                                        </MDTypography>

                                        {/* Deskripsi */}
                                        {dokumen.keterangan && (
                                          <MDBox mb={1.5} flex={1}>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              sx={{
                                                lineHeight: 1.6,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 4,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                minHeight: "4.8rem",
                                              }}
                                            >
                                              {dokumen.keterangan}
                                            </MDTypography>
                                          </MDBox>
                                        )}

                                        {/* Profil Pengupload */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={1.5}
                                        >
                                          {dokumen.uid_RSS ? (
                                            <img
                                              src={`https://smartdh.rotendaokab.go.id/uploads/${dokumen.uid_RSS}.jpg`}
                                              alt={dokumen.nama_user || "User"}
                                              style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                border: "1px solid #e0e0e0",
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                backgroundColor: "grey.200",
                                                color: "grey.500",
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 12 }}>
                                                person
                                              </Icon>
                                            </MDBox>
                                          )}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {dokumen.nama_user ||
                                              "Tidak diketahui"}
                                          </MDTypography>
                                        </MDBox>
                                      </MDBox>

                                      {/* Footer: Timestamp dan Action buttons */}
                                      <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mt="auto"
                                        pt={1}
                                        sx={{
                                          borderTop: "1px solid #e0e0e0",
                                        }}
                                      >
                                        {/* Timestamp */}
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {formatRelativeTime(
                                            dokumen.tanggal_upload
                                          )}
                                        </MDTypography>

                                        {/* Action buttons */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                        >
                                          {/* Icon Download/Buka File - selalu tampil */}
                                          <IconButton
                                            size="small"
                                            color="info"
                                            onClick={() =>
                                              handleFileClick(dokumen)
                                            }
                                            title={getFileButtonLabel(dokumen)}
                                          >
                                            {getFileIcon(dokumen)}
                                          </IconButton>

                                          {/* Icon Edit dan Hapus - hanya tampil jika user adalah pengupload */}
                                          {isUploader && (
                                            <>
                                              <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() =>
                                                  handleEdit(dokumen.id_dokumen)
                                                }
                                                title="Edit"
                                              >
                                                <Icon fontSize="small">
                                                  edit
                                                </Icon>
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                  handleDelete(
                                                    dokumen.id_dokumen,
                                                    dokumen.nama
                                                  )
                                                }
                                                title="Hapus"
                                              >
                                                <Icon fontSize="small">
                                                  delete
                                                </Icon>
                                              </IconButton>
                                            </>
                                          )}
                                        </MDBox>
                                      </MDBox>
                                    </MDBox>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        ) : (
                          // Tampilan List (memanjang) - dengan icon bulat di kiri
                          <>
                            {filteredDokumenList.map((dokumen) => {
                              const profileUrl = dokumen.uid_RSS
                                ? `https://smartdh.rotendaokab.go.id/uploads/${dokumen.uid_RSS}.jpg`
                                : null;
                              // Cek apakah user yang login adalah pengupload dokumen
                              const isUploader =
                                userId &&
                                dokumen.user_id &&
                                String(userId) === String(dokumen.user_id);

                              return (
                                <Card
                                  key={dokumen.id_dokumen}
                                  sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    boxShadow: 2,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <MDBox p={2.5}>
                                    <MDBox display="flex" gap={2}>
                                      {/* Icon/Logo di kiri */}
                                      <MDBox
                                        sx={{
                                          minWidth: 60,
                                          height: 60,
                                          borderRadius: "50%",
                                          backgroundColor: "#ffc107",
                                          border: "2px solid #ff0000",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <Icon
                                          sx={{
                                            fontSize: 30,
                                            color: "#000",
                                          }}
                                        >
                                          description
                                        </Icon>
                                      </MDBox>

                                      {/* Konten utama */}
                                      <MDBox flex={1}>
                                        {/* Header dengan judul dan timestamp */}
                                        <MDBox
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="start"
                                          mb={1}
                                        >
                                          <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="text"
                                            sx={{ flex: 1 }}
                                          >
                                            {dokumen.nama || "-"}
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ ml: 2 }}
                                          >
                                            {formatRelativeTime(
                                              dokumen.tanggal_upload
                                            )}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Source/Subtitle */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                          mb={1}
                                        >
                                          <Icon
                                            sx={{
                                              fontSize: 16,
                                              color: "text.secondary",
                                            }}
                                          >
                                            business
                                          </Icon>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {dokumen.nama_dinas || "-"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Profil Pengupload */}
                                        <MDBox
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          mb={1}
                                        >
                                          {dokumen.uid_RSS ? (
                                            <img
                                              src={`https://smartdh.rotendaokab.go.id/uploads/${dokumen.uid_RSS}.jpg`}
                                              alt={dokumen.nama_user || "User"}
                                              style={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                border: "1px solid #e0e0e0",
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                backgroundColor: "grey.200",
                                                color: "grey.500",
                                              }}
                                            >
                                              <Icon sx={{ fontSize: 14 }}>
                                                person
                                              </Icon>
                                            </MDBox>
                                          )}
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {dokumen.nama_user ||
                                              "Tidak diketahui"}
                                          </MDTypography>
                                        </MDBox>

                                        {/* Tags */}
                                        <MDBox
                                          display="flex"
                                          gap={1}
                                          flexWrap="wrap"
                                          mb={1.5}
                                        >
                                          <Chip
                                            label={
                                              dokumen.kategori
                                                ?.replace("_", " ")
                                                .replace(/\b\w/g, (l) =>
                                                  l.toUpperCase()
                                                ) || "-"
                                            }
                                            size="small"
                                            sx={{
                                              backgroundColor: "#fff59d",
                                              color: "#000",
                                              fontWeight: "medium",
                                              fontSize: "0.75rem",
                                            }}
                                          />
                                          <Chip
                                            icon={
                                              <Icon sx={{ fontSize: 16 }}>
                                                description
                                              </Icon>
                                            }
                                            label={
                                              dokumen.scope?.replace(
                                                /\b\w/g,
                                                (l) => l.toUpperCase()
                                              ) || "-"
                                            }
                                            size="small"
                                            sx={{
                                              backgroundColor: "#fff",
                                              color: "#000",
                                              fontWeight: "medium",
                                              fontSize: "0.75rem",
                                            }}
                                          />
                                        </MDBox>

                                        {/* Description */}
                                        {dokumen.keterangan && (
                                          <MDBox mb={1.5}>
                                            <MDTypography
                                              variant="body2"
                                              color="text"
                                              sx={{
                                                lineHeight: 1.6,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                              }}
                                            >
                                              {dokumen.keterangan}
                                            </MDTypography>
                                          </MDBox>
                                        )}

                                        {/* Action buttons - di kanan bawah */}
                                        <MDBox
                                          display="flex"
                                          justifyContent="flex-end"
                                          alignItems="center"
                                          gap={0.5}
                                          mt={1.5}
                                        >
                                          {/* Tombol Download/Buka File - selalu tampil */}
                                          <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            onClick={() =>
                                              handleFileClick(dokumen)
                                            }
                                            startIcon={getFileIcon(dokumen)}
                                            sx={{
                                              textTransform: "none",
                                              minWidth: "auto",
                                            }}
                                          >
                                            {getFileButtonLabel(dokumen)}
                                          </MDButton>

                                          {/* Tombol Edit dan Hapus - hanya tampil jika user adalah pengupload */}
                                          {isUploader && (
                                            <>
                                              <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() =>
                                                  handleEdit(dokumen.id_dokumen)
                                                }
                                                title="Edit"
                                              >
                                                <Icon fontSize="small">
                                                  edit
                                                </Icon>
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                  handleDelete(
                                                    dokumen.id_dokumen,
                                                    dokumen.nama
                                                  )
                                                }
                                                title="Hapus"
                                              >
                                                <Icon fontSize="small">
                                                  delete
                                                </Icon>
                                              </IconButton>
                                            </>
                                          )}
                                        </MDBox>
                                      </MDBox>
                                    </MDBox>
                                  </MDBox>
                                </Card>
                              );
                            })}
                          </>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            mt={3}
                            mb={2}
                          >
                            <MDPagination variant="gradient" color="info">
                              {currentPage > 1 && (
                                <MDPagination
                                  item
                                  onClick={() =>
                                    handlePageChange(currentPage - 1)
                                  }
                                >
                                  <Icon sx={{ fontWeight: "bold" }}>
                                    chevron_left
                                  </Icon>
                                </MDPagination>
                              )}

                              {/* Tampilkan beberapa halaman */}
                              {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                  let pageNum;
                                  if (totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                  } else {
                                    pageNum = currentPage - 2 + i;
                                  }

                                  return (
                                    <MDPagination
                                      key={pageNum}
                                      item
                                      onClick={() => handlePageChange(pageNum)}
                                      active={currentPage === pageNum}
                                    >
                                      {pageNum}
                                    </MDPagination>
                                  );
                                }
                              )}

                              {totalPages > 5 &&
                                currentPage < totalPages - 2 && (
                                  <>
                                    <MDPagination item disabled>
                                      ...
                                    </MDPagination>
                                    <MDPagination
                                      item
                                      onClick={() =>
                                        handlePageChange(totalPages)
                                      }
                                    >
                                      {totalPages}
                                    </MDPagination>
                                  </>
                                )}

                              {currentPage < totalPages && (
                                <MDPagination
                                  item
                                  onClick={() =>
                                    handlePageChange(currentPage + 1)
                                  }
                                >
                                  <Icon sx={{ fontWeight: "bold" }}>
                                    chevron_right
                                  </Icon>
                                </MDPagination>
                              )}
                            </MDPagination>

                            {/* Input untuk lompat ke halaman */}
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mt={2}
                            >
                              <MDTypography variant="caption" color="text">
                                Lompat ke halaman:
                              </MDTypography>
                              <MDInput
                                type="number"
                                value={currentPage}
                                onChange={(e) => {
                                  const page = parseInt(e.target.value);
                                  if (page >= 1 && page <= totalPages) {
                                    handlePageChange(page);
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                  max: totalPages,
                                  style: {
                                    width: "60px",
                                    textAlign: "center",
                                  },
                                }}
                                sx={{ width: "80px" }}
                              />
                              <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                onClick={() => handlePageChange(currentPage)}
                                sx={{ minWidth: "auto", px: 2 }}
                              >
                                Go
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        )}
                      </>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Modal Form Tambah/Edit Dokumen */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h5">
            {isEdit ? "Edit Dokumen" : "Tambah Dokumen"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  label="Nama Dokumen *"
                  value={form.nama}
                  onChange={(e) => setForm({ ...form, nama: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Keterangan"
                  value={form.keterangan}
                  onChange={(e) =>
                    setForm({ ...form, keterangan: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={kategoriOptions}
                  value={form.kategori || null}
                  onChange={(event, newValue) => {
                    setForm({ ...form, kategori: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Kategori *"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  getOptionLabel={(option) =>
                    option
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  }
                  disableClearable={false}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={scopeOptions}
                  value={form.scope || null}
                  onChange={(event, newValue) => {
                    setForm({ ...form, scope: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Scope *"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  getOptionLabel={(option) =>
                    option.replace(/\b\w/g, (l) => l.toUpperCase())
                  }
                  disableClearable={false}
                />
              </Grid>
              <Grid item xs={12}>
                <MDTypography
                  variant="caption"
                  color="text"
                  fontWeight="medium"
                  display="block"
                  mb={1}
                >
                  Upload File Dokumen (Maksimal 1.5 MB)
                </MDTypography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-upload-dokumen"
                  disabled={!!form.dokumen}
                />
                <label htmlFor="file-upload-dokumen">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    disabled={!!form.dokumen}
                    sx={{
                      color: form.dokumen ? "#ccc" : "#000000",
                      borderColor: form.dokumen ? "#ccc" : "#000000",
                    }}
                  >
                    {form.dokumen
                      ? "Upload File (tidak aktif - link sudah diisi)"
                      : "Pilih File atau Seret ke Sini"}
                  </Button>
                </label>
                {selectedFile && !fileSizeError && (
                  <MDBox
                    mt={1}
                    p={1.5}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDBox flex={1}>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          File terpilih: {selectedFile.name}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          display="block"
                        >
                          Ukuran: {(selectedFile.size / 1024 / 1024).toFixed(2)}{" "}
                          MB
                        </MDTypography>
                      </MDBox>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={handleRemoveFile}
                        sx={{ ml: 1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </MDBox>
                  </MDBox>
                )}
                {fileSizeError && (
                  <MDBox mt={1}>
                    <MDTypography variant="caption" color="error">
                      ⚠️ File melebihi 1.5 MB. Silakan gunakan link Google Drive
                      untuk file yang lebih besar.
                    </MDTypography>
                  </MDBox>
                )}
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label={
                    selectedFile
                      ? "Link Eksternal (tidak aktif - file sudah diupload)"
                      : "Link Eksternal (Google Drive, dll)"
                  }
                  value={form.dokumen}
                  onChange={handleLinkChange}
                  fullWidth
                  variant="outlined"
                  placeholder="https://drive.google.com/..."
                  disabled={!!selectedFile} // Disable jika ada file
                  helperText={
                    selectedFile
                      ? "Hapus file terlebih dahulu untuk menggunakan link"
                      : "Gunakan link Google Drive untuk file lebih dari 1.5 MB"
                  }
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseModal}>
            Batal
          </MDButton>
          <MDButton
            color="info"
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting ||
              fileSizeError ||
              (selectedFile && selectedFile.size > MAX_FILE_SIZE)
            }
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitting ? "Menyimpan..." : isEdit ? "Update" : "Simpan"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan file PDF (mengikuti pola dari daftar-penugasan) */}
      <Dialog
        open={openFileDialog}
        onClose={handleCloseFileDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Dokumen</DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            {selectedFileUrl ? (
              <iframe
                src={selectedFileUrl}
                width="100%"
                height="600px"
                title="PDF Viewer"
                style={{ border: "none" }}
              />
            ) : (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="600px"
              >
                <MDTypography variant="h6" color="text">
                  File tidak tersedia
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DaftarDokumen;
